/**
 * mSTEM robots reducer (GEN-03). Holds the in-memory robot inventory
 * surfaced by RobotManagerModal. Seeded at module load from
 * src/lib/mstem-mock/robots.js; can be replaced at runtime via SEED_ROBOTS
 * (used when hydrating from localStorage on RobotManagerModal mount).
 *
 * Security note (T-02-03-01): ADD_ROBOT and UPDATE_ROBOT validate the
 * incoming robot via `isValidRobot` — empty name, name > 100 chars,
 * missing id, or duplicate id all return the same state object reference
 * so callers cannot accidentally tamper with the list shape.
 */

import MOCK_ROBOTS from '../lib/mstem-mock/robots';

const ADD_ROBOT = 'scratch-gui/robots/ADD_ROBOT';
const UPDATE_ROBOT = 'scratch-gui/robots/UPDATE_ROBOT';
const DELETE_ROBOT = 'scratch-gui/robots/DELETE_ROBOT';
const SEED_ROBOTS = 'scratch-gui/robots/SEED_ROBOTS';

const MAX_NAME_LENGTH = 100;

const initialState = {
    list: MOCK_ROBOTS.slice()
};

const isValidRobot = function (robot) {
    if (!robot || typeof robot.name !== 'string') return false;
    const name = robot.name.trim();
    if (name.length === 0 || name.length > MAX_NAME_LENGTH) return false;
    if (typeof robot.id !== 'string' || robot.id.length === 0) return false;
    return true;
};

const reducer = function (state, action) {
    if (typeof state === 'undefined') state = initialState;
    switch (action.type) {
    case SEED_ROBOTS:
        if (!Array.isArray(action.list)) return state;
        return Object.assign({}, state, {
            list: action.list.slice()
        });
    case ADD_ROBOT:
        if (!isValidRobot(action.robot)) return state;
        // Reject duplicate ids — preserves uniqueness invariant for delete/edit by id.
        if (state.list.some(r => r.id === action.robot.id)) {
            return state;
        }
        return Object.assign({}, state, {
            list: state.list.concat([action.robot])
        });
    case UPDATE_ROBOT:
        if (!isValidRobot(action.robot)) return state;
        return Object.assign({}, state, {
            list: state.list.map(r => (
                r.id === action.robot.id ? Object.assign({}, r, action.robot) : r
            ))
        });
    case DELETE_ROBOT:
        if (typeof action.id !== 'string') return state;
        return Object.assign({}, state, {
            list: state.list.filter(r => r.id !== action.id)
        });
    default:
        return state;
    }
};

const addRobot = function (robot) {
    return {type: ADD_ROBOT, robot: robot};
};
const updateRobot = function (robot) {
    return {type: UPDATE_ROBOT, robot: robot};
};
const deleteRobot = function (id) {
    return {type: DELETE_ROBOT, id: id};
};
const seedRobots = function (list) {
    return {type: SEED_ROBOTS, list: list};
};

export {
    reducer as default,
    initialState as robotsInitialState,
    addRobot,
    updateRobot,
    deleteRobot,
    seedRobots
};
